/**
 * The assistant as a right-hand Sheet (#765, spec §8.0, D14): non-modal, so
 * the user keeps listening and reading while asking; closes only from the
 * cross, Escape or an entry button — never from a click outside, which a
 * non-modal Radix Dialog would otherwise treat as dismissal.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import type { ApiClient } from '@/lib/api';
import {
  useAssistantDrawerSafe,
  type AssistantDrawerContextValue,
} from '@/contexts/AssistantDrawerContext';
import { buildSubjectKey } from '@aica/api-types/schemas';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { AssistantPanel } from './AssistantPanel';
import { AssistantSession } from './AssistantSession';
import { ScopeSwitcher } from './ScopeSwitcher';

export interface AssistantDrawerProps {
  apiClient: ApiClient;
  selectedOrgId: string | null;
  /** Open/close and settled turns — the embedded host re-fits its iframe (§8.0). */
  onLayoutChange?: () => void;
  /** The host's whole-base page — see `ScopeSwitcher` (#1357). */
  onOpenWholeBase?: () => boolean;
}

export function AssistantDrawer(props: AssistantDrawerProps) {
  // Both layouts mount the drawer unconditionally; whether the assistant is
  // available is the provider's call, and it answers `null` when it is not
  // (owner's review, 17.09).
  const drawer = useAssistantDrawerSafe();
  if (!drawer) return null;
  return <AssistantSheet {...props} drawer={drawer} />;
}

function AssistantSheet({
  apiClient,
  selectedOrgId,
  onLayoutChange,
  onOpenWholeBase,
  drawer,
}: AssistantDrawerProps & { drawer: AssistantDrawerContextValue }) {
  const { t } = useTranslation('assistant');
  const { open, topic, offerTopic, offScreen, closeAssistant, openAssistant } = drawer;

  useEffect(() => {
    onLayoutChange?.();
  }, [open, onLayoutChange]);

  // An organization change closes the drawer in the provider, which outlives
  // this component (Codex P2, rounds 13 and 21). The topic itself is resolved
  // in the provider too — it reads the record on screen directly (#922) — and
  // so is a topic the subject's access refuses (#1645, #1677): the provider
  // leaves it, and never offers one.

  const sheet = (
    <Sheet
      open={open}
      modal={false}
      onOpenChange={(next) => {
        if (!next) closeAssistant();
      }}
    >
      <SheetContent
        side="right"
        size="xl"
        aria-label={t('a11y.panel')}
        data-testid="assistant-drawer"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-ai" aria-hidden="true" />
            {t('title')}
          </SheetTitle>
          {/* What the panel is about, always (#922). Without this line the
              assistant can answer about a record the user is no longer looking
              at and nothing on screen contradicts it.

              `SheetDescription` is the dialog's `aria-describedby`, announced
              once on open — but the topic now CHANGES while the panel stays
              open, so it is also a live region: otherwise a screen-reader user
              is the one person the fix leaves in the dark. The badge lives
              outside it because Radix renders the description as a `<p>`, and
              `Badge` is a `<div>`. */}
          <SheetDescription aria-live="polite" data-testid="assistant-topic">
            {topic ? topic.label || t('topic.unnamed') : t('description')}
          </SheetDescription>
          {offScreen && (
            <Badge
              variant="default"
              size="xs"
              className="self-start"
              aria-live="polite"
              data-testid="assistant-topic-offscreen"
            >
              {t('topic.offScreen')}
            </Badge>
          )}
          {/* The way back. The topic never switches itself while a paid turn is
              in flight, so this is how the user moves it on purpose. */}
          {offerTopic && (
            <button
              type="button"
              // The deliberate way out of a frozen topic (R8): the only
              // caller that moves the subject while a turn is in flight.
              onClick={() => openAssistant(offerTopic, { evenIfBusy: true })}
              className="self-start rounded-button text-xs text-ai underline underline-offset-2 transition-colors hover:text-ai-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="assistant-topic-offer"
            >
              {t('topic.offer', { label: offerTopic.label })}
            </button>
          )}
        </SheetHeader>
        {topic && (
          // Outside the keyed panel: a scope switch remounts the panel, and
          // the button just pressed must keep keyboard focus (#1041 review).
          <div className="border-b border-border px-4 py-3 sm:px-5 md:px-6">
            <ScopeSwitcher onOpenWholeBase={onOpenWholeBase} />
          </div>
        )}
        {topic ? (
          <AssistantPanel
            // The whole subject, not just the scope: a draft typed for record A
            // must not survive into record B's panel (Codex P2, round 2). The
            // label is deliberately NOT in the key — renaming a record must not
            // remount its conversation.
            key={`${selectedOrgId ?? ''}:${buildSubjectKey(topic.subject)}`}
            onLayoutChange={onLayoutChange}
          />
        ) : (
          <EmptyState icon={Sparkles} size="sm" title={t('openButtonNoSubject')} />
        )}
      </SheetContent>
    </Sheet>
  );
  if (!topic) return sheet;
  // The ask lifecycle lives outside the sheet: Radix unmounts the sheet's
  // content on close, and a question in flight — its promise, its key, the
  // turn being polled — must not go with it, or a reopen before history
  // lists the turn would take the same question again under a new key
  // (Codex P2, round 23). Not keyed by subject on purpose: the hook resets
  // itself on a subject or organization change, and the sheet stays put.
  return (
    <AssistantSession
      subject={topic.subject}
      apiClient={apiClient}
      selectedOrgId={selectedOrgId}
      enabled={open}
    >
      {sheet}
    </AssistantSession>
  );
}
